import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const CONSENT_KEY = 'rockhound_cookie_consent';

export default function CookieConsentBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) {
      setTimeout(() => {
        setIsVisible(true);
        setIsAnimating(true);
      }, 1000);

      // Auto-dismiss after 12 seconds
      const autoDismissTimer = setTimeout(() => {
        dismiss('accepted');
      }, 13000); // 1s delay + 12s = 13s total

      return () => clearTimeout(autoDismissTimer);
    }
  }, []);

  const dismiss = (value: 'accepted' | 'declined') => {
    localStorage.setItem(CONSENT_KEY, value);
    setIsAnimating(false);
    setTimeout(() => setIsVisible(false), 300);
  };

  if (!isVisible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      aria-live="polite"
      className={`fixed bottom-0 left-0 right-0 z-[9999] transition-transform duration-300 ease-out ${
        isAnimating ? 'translate-y-0' : 'translate-y-full'
      }`}
    >
      <div className="bg-[#0F1419] border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-gray-200">
            We use cookies to improve your experience.{' '}
            <Link
              to="/privacy-policy"
              className="text-[#FFC857] hover:text-[#FFD580] underline whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-[#FFC857] focus:ring-offset-2 focus:ring-offset-[#0F1419] rounded"
              aria-label="Read our privacy policy"
            >
              Privacy Policy
            </Link>
            {' · '}
            <Link
              to="/privacy-policy#ccpa"
              className="text-[#FFC857] hover:text-[#FFD580] underline whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-[#FFC857] focus:ring-offset-2 focus:ring-offset-[#0F1419] rounded"
              aria-label="California privacy rights"
            >
              Do Not Sell My Info
            </Link>
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => dismiss('declined')}
              className="text-xs text-gray-100 hover:text-white transition-colors whitespace-nowrap cursor-pointer focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 focus:ring-offset-[#0F1419] rounded px-3 py-3 min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Decline cookies"
            >
              Decline
            </button>
            <button
              onClick={() => dismiss('accepted')}
              className="text-xs font-semibold px-5 py-3 min-h-[44px] min-w-[44px] flex items-center justify-center bg-[#FFC857] text-[#0F1419] rounded-full hover:bg-[#FFD580] transition-colors whitespace-nowrap cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#FFC857] focus:ring-offset-2 focus:ring-offset-[#0F1419]"
              aria-label="Accept cookies"
            >
              Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}